// Tabela da Consulta: estados skeleton/empty/error+retry,
// paginação pela URL e navegação para a PÁGINA de detalhe do envio
// (?from= preserva a busca atual para o botão "Voltar").
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useLocation } from "react-router-dom"

import { Button } from "@/components/ui/button"
import { DataTable } from "@/components/ui/data-table"
import { Skeleton } from "@/components/ui/skeleton"
import { TooltipProvider } from "@/components/ui/tooltip"
import { useAuth } from "@/contexts/auth"
import { useNotificationCenterNotificationsSearchParams } from "@/hooks"

import { useNotificationCenterNotifications } from "../hooks"
import { getNotificationSearchColumns } from "./notification-search-columns"

// Skeleton no formato real da tabela (padrão Pay2B).
const NotificationsTableSkeleton = () => (
  <div
    data-testid="notifications-search-skeleton"
    className="space-y-2 rounded-md border p-4"
  >
    <Skeleton className="h-8 w-full" />
    {Array.from({ length: 8 }).map((_, index) => (
      <Skeleton
        key={`notification-skeleton-${index}`}
        className="h-10 w-full"
      />
    ))}
  </div>
)

export const NotificationsSearchTable = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const { selectedWhiteLabel } = useAuth()
  const { queryFilters, setParams } =
    useNotificationCenterNotificationsSearchParams()

  // queryFilters já resolve o dia (?date → startDate=endDate) e os
  // filtros legados de deep-link. WL é ABSORVIDO do seletor global
  // (useAuth) — SEMPRE enviado; ausente = "Todos".
  const { data, error, isLoading, refetch } =
    useNotificationCenterNotifications({
      ...queryFilters,
      whiteLabelId: selectedWhiteLabel?.uuid,
    })

  // Navega para a página de detalhe do ENVIO (schedulerId — o payload
  // único já traz todos os canais) carregando a busca atual em ?from=,
  // para o "Voltar para a consulta" restaurar os filtros.
  const columns = useMemo(
    () =>
      getNotificationSearchColumns(t, (schedulerId) => {
        const fromQuery = location.search
          ? `?from=${encodeURIComponent(location.search)}`
          : ""
        return `/notification-center/notifications/${schedulerId}${fromQuery}`
      }),
    [t, location.search]
  )

  if (isLoading) {
    return <NotificationsTableSkeleton />
  }

  if (error) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-md border border-red-200 bg-red-50 p-6">
        <p className="text-center text-sm text-red-700">
          {t(
            "pages.notificationCenter.searchPage.loadError"
          )}
        </p>
        <Button
          type="button"
          variant="outline"
          onClick={() => refetch()}
        >
          {t("pages.notificationCenter.searchPage.retry")}
        </Button>
      </div>
    )
  }

  if (!data?.content.length) {
    return (
      <p className="rounded-md border p-8 text-center text-sm text-muted-foreground">
        {t(
          "pages.notificationCenter.searchPage.emptyState"
        )}
      </p>
    )
  }

  const { page } = data
  const isFirstPage = page.number <= 0
  const isLastPage = page.number >= page.totalPages - 1

  return (
    <TooltipProvider>
      <div className="space-y-3">
        <DataTable
          data={data.content}
          columns={columns}
          showColumnVisibility
        />
        <nav
          aria-label={t(
            "pages.notificationCenter.searchPage.pagination.label"
          )}
          className="flex items-center justify-between"
        >
          <span className="text-sm tabular-nums text-muted-foreground">
            {t(
              "pages.notificationCenter.searchPage.pagination.summary",
              {
                page: page.number + 1,
                totalPages: page.totalPages,
                totalElements: page.totalElements,
              }
            )}
          </span>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={isFirstPage}
              onClick={() =>
                setParams({
                  page: String(page.number - 1),
                })
              }
            >
              {t(
                "pages.notificationCenter.searchPage.pagination.previous"
              )}
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={isLastPage}
              onClick={() =>
                setParams({
                  page: String(page.number + 1),
                })
              }
            >
              {t(
                "pages.notificationCenter.searchPage.pagination.next"
              )}
            </Button>
          </div>
        </nav>
      </div>
    </TooltipProvider>
  )
}
